import React, { useState } from 'react';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import PropertyList from '@dashboard/components/PropertyList';
import { Badge } from '@dashboard/components/ui/badge';
import getUrlFromCdn from '@dashboard/services/getUrlFromCdn';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';

const IMAGE_SIZE = 40;

// A deleted or moved file would otherwise show the browser's broken-image icon.
export const Thumbnail = ({ url }) => {
  const [hasFailed, setHasFailed] = useState(false);

  if (!url || hasFailed) {
    return (
      <div
        data-testid="order-item-placeholder"
        className="size-10 shrink-0 rounded-md bg-gray-100 dark:bg-muted"
      />
    );
  }

  return (
    <img
      src={getUrlFromCdn(url, { width: IMAGE_SIZE * 2, height: IMAGE_SIZE * 2 })}
      alt=""
      width={IMAGE_SIZE}
      height={IMAGE_SIZE}
      onError={() => setHasFailed(true)}
      className="size-10 shrink-0 rounded-md object-cover bg-gray-100 dark:bg-muted"
    />
  );
};

export function describeOptions(variants = []) {
  return variants.flatMap(variant => {
    if (variant.selectedOptions?.length) {
      return variant.selectedOptions.map(option =>
        (option.quantity ?? 1) > 1
          ? `${option.quantity} x ${option.optionName}`
          : option.optionName,
      );
    }

    return variant.selectedOptionName ? [variant.selectedOptionName] : [];
  });
}

const OrderItemsCard = ({
  title = 'Items',
  items,
  currency,
  bumpLabel = 'Order bump',
  interval,
  intervalCount,
}) => {
  if (!items?.length) return null;

  const hasImages = items.some(item => item.imageUrl);

  const price = (amount, item) =>
    getPriceLabel({
      amount,
      currency,
      ...(item.billingType === 'recurring' && interval
        ? { type: 'subscription', interval, intervalCount }
        : {}),
    });

  return (
    <Card className="mx-auto">
      <CardContent>
        <PropertyList.Title>{title}</PropertyList.Title>

        <ul>
          {items.map((item, index) => (
            <li
              key={index}
              data-testid="order-item"
              className="flex items-start gap-x-3 py-3 border-b border-gray-100 dark:border-border"
            >
              {hasImages && <Thumbnail url={item.imageUrl} />}

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold break-words">{item.name}</span>
                  {item.isOrderBump && item.type !== 'setup_fee' && bumpLabel && (
                    <Badge variant="info">{bumpLabel}</Badge>
                  )}
                </div>
                {(item.type !== 'setup_fee' || item.quantity > 1) && (
                  <div className="text-sm text-gray-500 dark:text-muted-foreground">
                    {[
                      `${item.quantity} × ${price(item.unitPrice, item)}`,
                      ...describeOptions(item.variants),
                    ].join(' · ')}
                  </div>
                )}
              </div>

              <div className="whitespace-nowrap text-right">{price(item.amount, item)}</div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default OrderItemsCard;
